import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Truck, DollarSign, Calculator, Save, Loader2 } from "lucide-react";
import { fetchDeliveryFee, updateDeliveryFee } from "@/api/setting-api";
import { SettingDeliveryFee } from "@/types/setting-type";
import { toast } from "@/hooks/use-toast";

export function ShippingDeliverySetting() {
    const [defaultShipping, setDefaultShipping] = useState<number>(2.0);
    const [defaultShippingCost, setDefaultShippingCost] = useState<number>(0.25);
    const [isPending, setIsPending] = useState<boolean>(false);
    const handleSaveShippingDelivery = async () => {
        setIsPending(true);
        try {
            const data: SettingDeliveryFee = {
                price: defaultShipping,
                cost: defaultShippingCost
            };
            const resp = await updateDeliveryFee(data);
            if (resp.success) {
                toast({
                    title: "Success",
                    description: "Delivery fee updated successfully",
                });
            }

        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to update delivery fee",
            });
        }
        setIsPending(false);


    };

    useEffect(() => {
        const getDeliveryFee = async () => {
            const resp = await fetchDeliveryFee();
            if (resp.success) {
                const { price, cost } = resp.data;
                setDefaultShipping(price);
                setDefaultShippingCost(cost);
            }
        };
        getDeliveryFee();
    }, []);

    const deliveryMargin = defaultShipping - defaultShippingCost;

    return (
        <Card className="shadow-sm border-slate-200">
            <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-blue-600" />
                    <CardTitle>Shipping & Delivery</CardTitle>
                </div>
                <CardDescription>
                    Set your standard rates for shipping fees and internal delivery costs.
                </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                        <Label htmlFor="default-shipping" className="text-slate-700 font-semibold text-sm">
                            Default Shipping Fee
                        </Label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                <DollarSign className="h-4 w-4" />
                            </div>
                            <Input
                                id="default-shipping"
                                type="number"
                                min="0"
                                step="0.01"
                                value={defaultShipping}
                                onChange={(e) => setDefaultShipping(parseFloat(e.target.value) || 0)}
                                className="pl-9 bg-white border-slate-200 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                            />
                        </div>
                        <p className="text-[11px] text-slate-500">The amount charged to the customer.</p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="default-shipping-cost" className="text-slate-700 font-semibold text-sm">
                            Default Shipping Cost
                        </Label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                <DollarSign className="h-4 w-4" />
                            </div>
                            <Input
                                id="default-shipping-cost"
                                type="number"
                                min="0"
                                step="0.01"
                                value={defaultShippingCost}
                                onChange={(e) => setDefaultShippingCost(parseFloat(e.target.value) || 0)}
                                className="pl-9 bg-white border-slate-200 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                            />
                        </div>
                        <p className="text-[11px] text-slate-500">Your internal expense per delivery.</p>
                    </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                            <Calculator className="w-4 h-4" />
                        </div>
                        <div>
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Net Shipping Margin</p>
                            <p className="text-lg font-bold text-slate-900">${deliveryMargin.toFixed(2)}</p>
                        </div>
                    </div>
                    <div className={`text-xs font-bold px-2 py-1 rounded ${deliveryMargin >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {deliveryMargin >= 0 ? 'PROFITABLE' : 'LOSS'}
                    </div>
                </div>

                {/* Save Button */}
                <div className="pt-2 border-t flex justify-end">
                    <Button
                        disabled={isPending}
                        onClick={handleSaveShippingDelivery}
                        className="min-w-[140px] font-semibold shadow-sm active:scale-95 transition-all"
                    >
                        {isPending ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
                        ) : (
                            <><Save className="mr-2 h-4 w-4" /> Save Settings</>
                        )}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}